import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import ReactTooltip from 'react-tooltip';
import { getSortByString, Pill, TableState } from '@nbirl/nbi-common-ui-libs';

import { useQueryParams } from '../../../../../shared/hooks';
import { Folder } from '../../../folders/models';
import { useFolderContentQuery } from '../../../folders/queries';
import { DocumentsListState } from '../../models';
import { useDocumentsQuery } from '../../queries';
import {
  getDocumentListColumns,
  getDocumentListData,
  getDocumentRowCells,
} from '../../utils';
import { DocumentsListTable } from './document-list-table';
import { DocumentsListFilters } from './documents-list-filters.component';
import { ShareDocumentModal } from './share-document-modal';
import { SharedDocumentTable } from './shared-document-table.component';

const DEFAULT_LIST_STATE: DocumentsListState = {
  pageSize: 50,
  pageIndex: 0,
  sortBy: getSortByString({ id: 'name', desc: false }),
  search: '',
};

type DocumentListProps = {
  folder: Folder;
  onFolderTreeChange: () => void;
  onFolderChange: (id: string) => void;
};

export const DocumentsList: React.FC<DocumentListProps> = ({
  folder,
  onFolderTreeChange,
  onFolderChange,
}) => {
  const [shareDocumentId, setShareDocumentId] = useState<string | null>(null);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const documentId = searchParams.get('documentId');
  const userId = searchParams.get('userId');
  const [updatingStatuses, setUpdatingStatuses] = useState<string[]>([]);
  const [rowIds, setRowIds] = useState<Record<string, boolean>>({});
  const [query, setQuery] =
    useQueryParams<DocumentsListState>(DEFAULT_LIST_STATE);
  const { search, ...tableState } = query;
  const isSearching = Boolean(search);

  const {
    refetch: refetchContentQuery,
    remove: removeContentQuery,
    ...contentQuery
  } = useFolderContentQuery(folder.id, { enabled: !isSearching });

  const {
    refetch: refetchDocumentsQuery,
    remove: removeDocumentsQuery,
    ...documentsQuery
  } = useDocumentsQuery(
    { ...query, folderId: folder.id },
    { enabled: isSearching }
  );

  const refetch = useCallback(() => {
    if (isSearching) {
      refetchDocumentsQuery();
      removeContentQuery();
    } else {
      refetchContentQuery();
      removeDocumentsQuery();
    }
  }, [
    isSearching,
    refetchContentQuery,
    removeContentQuery,
    refetchDocumentsQuery,
    removeDocumentsQuery,
  ]);

  const handleFolderTreeChange = useCallback(() => {
    onFolderTreeChange();
    refetch();
  }, [refetch, onFolderTreeChange]);

  const columns = useMemo(
    () =>
      getDocumentListColumns({
        isSearching,
        updatingStatuses,
        subFolders: contentQuery.data?.subFolders || [],
        onDocumentStatusUpdateStart: (id) =>
          setUpdatingStatuses((statuses) => [...statuses, id]),
        onDocumentStatusUpdateFinish: (id) =>
          setUpdatingStatuses((statuses) =>
            statuses.filter((status) => status !== id)
          ),
        onFolderChange,
        onFolderTreeChange: handleFolderTreeChange,
        onRefetchList: refetch,
        onShareDocument: setShareDocumentId,
      }),
    [
      isSearching,
      updatingStatuses,
      refetch,
      handleFolderTreeChange,
      contentQuery.data?.subFolders,
      onFolderChange,
    ]
  );

  const [documentListRowCells, setDocumentListRowCells] =
    useState<NodeListOf<HTMLTableCellElement>>();

  const documentListContainerRef = useCallback(
    (el: HTMLDivElement | null) => {
      if (el) {
        const cells = getDocumentRowCells(el);

        if (cells) {
          setDocumentListRowCells(cells);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columns]
  );

  const data = getDocumentListData(
    isSearching,
    tableState.sortBy,
    folder.id,
    documentsQuery.data?.content,
    contentQuery.data?.subFolders,
    contentQuery.data?.documents
  );

  const sharedDocumentData = useMemo(
    () => contentQuery.data?.documents?.find((doc) => doc.id === documentId),
    [contentQuery.data, documentId]
  );

  const hasSharedDocument = Boolean(userId && documentId);

  const handleStateChange = ({ selectedRowIds, ...state }: TableState) => {
    setQuery({ ...query, ...state });
    setRowIds(selectedRowIds);
  };

  useEffect(() => {
    ReactTooltip.rebuild();
  }, [columns, query]);

  useEffect(() => {
    if (Object.values(rowIds).length > 0) {
      setRowIds({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folder]);

  return (
    <>
      <div className="max-w-7xl">
        <DocumentsListFilters
          folder={folder}
          search={query.search}
          onSearchChange={(value) =>
            setQuery({ ...query, search: value, pageIndex: 0 })
          }
          onCreateFolderSuccess={handleFolderTreeChange}
        />

        {hasSharedDocument && (
          <SharedDocumentTable
            documentListRowCells={documentListRowCells}
            userId={userId || ''}
            documentId={documentId || ''}
            documentData={sharedDocumentData}
            columns={[{ id: 'selection' }, ...columns]}
          />
        )}
      </div>

      {isSearching && documentsQuery.isSuccess && (
        <Pill
          className="mb-5"
          variant="gray"
        >{`Documents Found (${documentsQuery.data.totalElements})`}</Pill>
      )}

      <div ref={documentListContainerRef}>
        <DocumentsListTable
          columns={columns}
          data={data}
          isLoading={
            isSearching ? documentsQuery.isLoading : contentQuery.isLoading
          }
          error={isSearching ? documentsQuery.error : contentQuery.error}
          isSearching={isSearching}
          totalCount={
            isSearching && documentsQuery.isSuccess
              ? documentsQuery.data.totalElements
              : 0
          }
          pageCount={
            isSearching && documentsQuery.isSuccess
              ? documentsQuery.data.totalPages
              : 0
          }
          selectedRowIds={rowIds}
          hasSharedDocument={hasSharedDocument}
          tableState={tableState}
          onStateChange={handleStateChange}
          onDeselectRows={() => setRowIds({})}
          onBulkStatusChange={() => refetch()}
        />
      </div>

      {shareDocumentId && (
        <ShareDocumentModal
          isPending={
            data.find((row) => row.id === shareDocumentId)?.status === 'PENDING'
          }
          folderId={folder.id}
          documentId={shareDocumentId}
          onDismiss={() => setShareDocumentId(null)}
        />
      )}
    </>
  );
};
